import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Image,
  SimpleGrid,
  Center,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
  Spinner,
  useToast,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from '@chakra-ui/react';
import { FaMagic, FaUsers, FaArrowLeft } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchDeckById } from '../utils/deckApi';

export default function DeckDetail() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [deck, setDeck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('visuale');

  // Colori Magic per i badge
  const colorOptions = [
    { value: 'W', label: 'Bianco', color: 'yellow', manaColor: '#F8F6D8' },
    { value: 'U', label: 'Blu', color: 'blue', manaColor: '#C1D7E9' },
    { value: 'B', label: 'Nero', color: 'gray', manaColor: '#A69F9D' },
    { value: 'R', label: 'Rosso', color: 'red', manaColor: '#E4998C' },
    { value: 'G', label: 'Verde', color: 'green', manaColor: '#A3C095' },
    { value: 'C', label: 'Incolore', color: 'gray', manaColor: '#C9C5C4' },
  ];

  // Funzione per ottenere il colore del badge del formato
  const getFormatoColor = (formato) => {
    const colorMap = {
      'Commander Tradizionale (EDH)': 'orange',
      'Duel Commander': 'red',
      Pauper: 'yellow',
      Standard: 'green',
      Historic: 'teal',
      Legacy: 'purple',
      Altro: 'gray',
    };
    return colorMap[formato] || 'gray';
  };

  // Funzione per ottenere il colore del badge del bracket
  const getBracketColor = (bracket) => {
    const colorMap = {
      'Bracket 1': 'red',
      'Bracket 2': 'orange',
      'Bracket 3': 'yellow',
      'Bracket 4': 'green',
      'Bracket 5': 'blue',
    };
    return colorMap[bracket] || 'gray';
  };

  // Funzione per renderizzare i colori del mazzo
  const renderDeckColors = (colors) => {
    if (!colors) return null;

    const colorSymbols = colors.replace(/[{}]/g, '');
    return (
      <HStack spacing={1}>
        {colorSymbols.split('').map((color) => {
          const colorInfo = colorOptions.find((c) => c.value === color);
          if (!colorInfo) return null;

          return (
            <Badge
              key={`${color}-${colorInfo.label}`}
              colorScheme={colorInfo.color}
              variant="solid"
              size="sm"
              px={2}
              py={1}
              bg={colorInfo.manaColor}
              color="black"
            >
              {color}
            </Badge>
          );
        })}
      </HStack>
    );
  };

  // Funzione per raggruppare le carte per tipo usando i dati Scryfall
  const groupCardsByType = () => {
    if (!deck?.deck || !Array.isArray(deck.deck)) return {};

    const grouped = {
      Creature: [],
      Instant: [],
      Sorcery: [],
      Enchantment: [],
      Artifact: [],
      Land: [],
      Planeswalker: [],
      Tribal: [],
      Other: [],
    };

    deck.deck.forEach((card) => {
      let type = 'Other';

      if (card.type_line) {
        const typeLine = card.type_line.toLowerCase();
        if (typeLine.includes('creature')) type = 'Creature';
        else if (typeLine.includes('instant')) type = 'Instant';
        else if (typeLine.includes('sorcery')) type = 'Sorcery';
        else if (typeLine.includes('enchantment')) type = 'Enchantment';
        else if (typeLine.includes('artifact')) type = 'Artifact';
        else if (typeLine.includes('land')) type = 'Land';
        else if (typeLine.includes('planeswalker')) type = 'Planeswalker';
        else if (typeLine.includes('tribal')) type = 'Tribal';
      }

      grouped[type].push(card);
    });

    return grouped;
  };

  // Funzione per calcolare le statistiche del mazzo dai dati Scryfall
  const calculateDeckStats = () => {
    if (!deck?.deck || !Array.isArray(deck.deck)) return null;

    const cards = deck.deck;
    const totalCards = cards.reduce(
      (sum, card) => sum + (card.quantity || 1),
      0
    );
    const totalLands = cards
      .filter((card) => card.type_line?.toLowerCase().includes('land'))
      .reduce((sum, card) => sum + (card.quantity || 1), 0);

    const totalCreatures = cards
      .filter((card) => card.type_line?.toLowerCase().includes('creature'))
      .reduce((sum, card) => sum + (card.quantity || 1), 0);

    const totalSpells = totalCards - totalLands;
    const averageCMC =
      cards.reduce((sum, card) => sum + card.cmc * (card.quantity || 1), 0) /
      totalCards;

    // Estrai colori dal mana cost
    const colorSymbols = new Set();
    cards.forEach((card) => {
      if (card.mana_cost) {
        const matches = card.mana_cost.match(/[WUBRG]/g);
        if (matches) {
          matches.forEach((symbol) => colorSymbols.add(symbol));
        }
      }
    });

    const colors = Array.from(colorSymbols).sort().join('');
    const colorDistribution = { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 };

    cards.forEach((card) => {
      if (card.colors && Array.isArray(card.colors)) {
        card.colors.forEach((color) => {
          if (Object.prototype.hasOwnProperty.call(colorDistribution, color)) {
            colorDistribution[color] += card.quantity || 1;
          }
        });
      }
    });

    return {
      totalCards,
      totalLands,
      totalCreatures,
      totalSpells,
      averageCMC: Math.round(averageCMC * 100) / 100,
      colors,
      colorDistribution,
      cardCount: cards.length,
    };
  };

  // Carica i dati del deck
  useEffect(() => {
    const loadDeck = async () => {
      if (!deckId) return;

      setLoading(true);
      try {
        const deckData = await fetchDeckById(deckId);
        setDeck(deckData);
      } catch (error) {
        toast({
          title: 'Errore',
          description: 'Impossibile caricare i dettagli del mazzo',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        navigate('/decks');
      } finally {
        setLoading(false);
      }
    };

    loadDeck();
  }, [deckId, navigate, toast]);

  if (loading) {
    return (
      <Center py={20}>
        <VStack spacing={4}>
          <Spinner size="xl" color="purple.500" />
          <Text color="gray.600">Caricamento mazzo...</Text>
        </VStack>
      </Center>
    );
  }

  if (!deck) {
    return (
      <Center py={20}>
        <VStack spacing={4}>
          <Text color="gray.600">Mazzo non trovato</Text>
          <Button onClick={() => navigate('/decks')}>Torna ai Mazzi</Button>
        </VStack>
      </Center>
    );
  }

  const groupedCards = groupCardsByType();
  const deckStats = calculateDeckStats();

  if (!deckStats) {
    return (
      <Center py={20}>
        <Text color="gray.600">Nessun dato disponibile per questo mazzo</Text>
      </Center>
    );
  }

  return (
    <Box bg="transparent" minH="100vh" overflowX="hidden" p={4}>
      <Container maxW="container.xl" py={8}>
        {/* Breadcrumb */}
        <Breadcrumb mb={6}>
          <BreadcrumbItem>
            <BreadcrumbLink onClick={() => navigate('/decks')}>
              Mazzi
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>{deck.nomeMazzo}</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Header */}
        <Box bg="white" boxShadow="lg" borderRadius="lg" px={8} py={8} mb={6}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Heading
                as="h1"
                size="xl"
                color="blue.600"
                fontFamily="Poppins, sans-serif"
              >
                🃏 {deck.nomeMazzo}
              </Heading>

              <Button
                leftIcon={<FaArrowLeft />}
                variant="outline"
                onClick={() => navigate('/decks')}
              >
                Torna ai Mazzi
              </Button>
            </HStack>

            <HStack spacing={3} wrap="wrap">
              {deck.formato && (
                <Badge
                  colorScheme={getFormatoColor(deck.formato)}
                  variant="solid"
                  size="md"
                >
                  {deck.formato}
                </Badge>
              )}

              {deck.bracket && (
                <Badge
                  colorScheme={getBracketColor(deck.bracket)}
                  variant="outline"
                  size="md"
                >
                  {deck.bracket}
                </Badge>
              )}

              {deck.colors && (
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Colori:
                  </Text>
                  {renderDeckColors(deck.colors)}
                </Box>
              )}
            </HStack>
          </VStack>
        </Box>

        {/* Contenuto principale */}
        <VStack spacing={6} align="stretch">
          {/* Informazioni utente */}
          <Box
            p={4}
            bg="blue.50"
            borderRadius="md"
            border="1px solid"
            borderColor="blue.200"
          >
            <HStack spacing={4} justify="space-between">
              <HStack spacing={2}>
                <Icon as={FaUsers} color="blue.500" />
                <Text fontSize="md">
                  Creato da:{' '}
                  <strong>
                    {deck.user?.firstName && deck.user?.lastName
                      ? `${deck.user.firstName} ${deck.user.lastName}`
                      : deck.user?.data?.firstName && deck.user?.data?.lastName
                        ? `${deck.user.data.firstName} ${deck.user.data.lastName}`
                        : deck.socio?.firstName && deck.socio?.lastName
                          ? `${deck.socio.firstName} ${deck.socio.lastName}`
                          : deck.socio?.data?.firstName &&
                              deck.socio?.data?.lastName
                            ? `${deck.socio.data.firstName} ${deck.socio.data.lastName}`
                            : deck.user?.username ||
                              deck.user?.data?.username ||
                              deck.socio?.username ||
                              deck.socio?.data?.username ||
                              'Utente'}
                  </strong>
                </Text>
              </HStack>

              {deck.createdAt && (
                <HStack spacing={2}>
                  <Icon as={FaMagic} color="blue.500" />
                  <Text fontSize="sm">
                    Data creazione:{' '}
                    <strong>
                      {new Date(deck.createdAt).toLocaleDateString('it-IT')}
                    </strong>
                  </Text>
                </HStack>
              )}
            </HStack>
          </Box>

          {/* Descrizione */}
          {deck.description && (
            <Box
              p={4}
              bg="blue.50"
              borderRadius="md"
              border="1px solid"
              borderColor="blue.200"
            >
              <Text fontSize="md" color="blue.700" lineHeight="1.6">
                {deck.description}
              </Text>
            </Box>
          )}

          {/* Statistiche del mazzo */}
          <Box
            p={4}
            bg="green.50"
            borderRadius="md"
            border="1px solid"
            borderColor="green.200"
          >
            <Text fontWeight="semibold" color="green.700" mb={3}>
              📊 Statistiche del Mazzo
            </Text>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Box textAlign="center">
                <Text fontSize="lg" fontWeight="bold" color="green.600">
                  {deckStats.totalCards}
                </Text>
                <Text fontSize="sm" color="green.700">
                  Totale Carte
                </Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="lg" fontWeight="bold" color="blue.600">
                  {deckStats.totalLands}
                </Text>
                <Text fontSize="sm" color="blue.700">
                  Terre
                </Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="lg" fontWeight="bold" color="red.600">
                  {deckStats.totalCreatures}
                </Text>
                <Text fontSize="sm" color="red.700">
                  Creature
                </Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="lg" fontWeight="bold" color="purple.600">
                  {deckStats.averageCMC}
                </Text>
                <Text fontSize="sm" color="purple.700">
                  CMC Medio
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Tabs per organizzare il contenuto */}
          <Box bg="white" borderRadius="lg" boxShadow="lg" overflow="hidden">
            <Tabs variant="enclosed" value={activeTab} onChange={setActiveTab}>
              <TabList>
                <Tab>🎴 Visuale Carte</Tab>
                <Tab>📈 Analisi</Tab>
              </TabList>

              <TabPanels>
                {/* Tab: Visuale Carte */}
                <TabPanel>
                  <VStack spacing={6} align="stretch">
                    {Object.entries(groupedCards).map(([type, cards]) => {
                      if (cards.length === 0) return null;

                      return (
                        <Box key={type}>
                          <Text
                            fontWeight="semibold"
                            fontSize="lg"
                            mb={4}
                            color="blue.600"
                          >
                            {type} (
                            {cards.reduce(
                              (total, card) => total + (card.quantity || 1),
                              0
                            )}
                            )
                          </Text>
                          <SimpleGrid
                            columns={{ base: 2, md: 3, lg: 4 }}
                            spacing={4}
                          >
                            {cards.map((card) => (
                              <Box
                                key={`${card.nome || card.name}-${card.set || 'default'}-${card.quantity || 1}`}
                                textAlign="center"
                                position="relative"
                              >
                                {card.image_uris?.normal ||
                                card.image_uris?.small ? (
                                  <Image
                                    src={
                                      card.image_uris.normal ||
                                      card.image_uris.small
                                    }
                                    alt={card.nome || card.name}
                                    borderRadius="md"
                                    boxShadow="md"
                                    _hover={{
                                      transform: 'scale(1.05)',
                                      transition: 'transform 0.2s',
                                    }}
                                    cursor="pointer"
                                    onClick={() =>
                                      window.open(
                                        card.image_uris.normal ||
                                          card.image_uris.small,
                                        '_blank'
                                      )
                                    }
                                  />
                                ) : (
                                  <Box
                                    p={4}
                                    bg="gray.100"
                                    borderRadius="md"
                                    border="2px dashed"
                                    borderColor="gray.300"
                                    minH="200px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <Text color="gray.500" fontSize="sm">
                                      {card.nome || card.name}
                                    </Text>
                                  </Box>
                                )}

                                <HStack justify="center" mt={2} spacing={2}>
                                  <Badge colorScheme="blue" variant="outline">
                                    {card.quantity || 1}x
                                  </Badge>
                                  {card.set && (
                                    <Badge
                                      colorScheme="purple"
                                      variant="outline"
                                    >
                                      {card.set.toUpperCase()}
                                    </Badge>
                                  )}
                                  {card.cmc > 0 && (
                                    <Badge
                                      colorScheme="green"
                                      variant="outline"
                                    >
                                      CMC: {card.cmc}
                                    </Badge>
                                  )}
                                  {card.rarity && (
                                    <Badge
                                      colorScheme="orange"
                                      variant="outline"
                                    >
                                      {card.rarity}
                                    </Badge>
                                  )}
                                </HStack>

                                {/* Informazioni aggiuntive della carta */}
                                <Box
                                  mt={2}
                                  p={2}
                                  bg="gray.50"
                                  borderRadius="md"
                                >
                                  <Text
                                    fontSize="xs"
                                    color="gray.600"
                                    noOfLines={2}
                                  >
                                    {card.type_line}
                                  </Text>
                                  {card.mana_cost && (
                                    <Text fontSize="xs" color="gray.600" mt={1}>
                                      {card.mana_cost}
                                    </Text>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </SimpleGrid>
                        </Box>
                      );
                    })}
                  </VStack>
                </TabPanel>

                {/* Tab: Analisi */}
                <TabPanel>
                  <VStack spacing={6} align="stretch">
                    {/* Distribuzione colori */}
                    {Object.keys(deckStats.colorDistribution).length > 0 && (
                      <Box
                        p={4}
                        bg="purple.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="purple.200"
                      >
                        <Text fontWeight="semibold" color="purple.700" mb={3}>
                          🎨 Distribuzione Colori
                        </Text>
                        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                          {Object.entries(deckStats.colorDistribution).map(
                            ([color, count]) => {
                              const colorInfo = colorOptions.find(
                                (c) => c.value === color
                              );
                              return (
                                <HStack key={color} justify="space-between">
                                  <HStack spacing={2}>
                                    <Badge
                                      colorScheme={colorInfo?.color || 'gray'}
                                      variant="solid"
                                      size="sm"
                                      bg={colorInfo?.manaColor || 'gray.200'}
                                      color="black"
                                    >
                                      {color}
                                    </Badge>
                                    <Text fontSize="sm">
                                      {colorInfo?.label}
                                    </Text>
                                  </HStack>
                                  <Text fontWeight="bold">{count}</Text>
                                </HStack>
                              );
                            }
                          )}
                        </SimpleGrid>
                      </Box>
                    )}

                    {/* Curva di mana */}
                    <Box
                      p={4}
                      bg="orange.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="orange.200"
                    >
                      <Text fontWeight="semibold" color="orange.700" mb={3}>
                        📈 Curva di Mana
                      </Text>
                      <Text fontSize="sm" color="orange.700">
                        CMC Medio: <strong>{deckStats.averageCMC}</strong>
                      </Text>
                      <Text fontSize="sm" color="orange.700">
                        Terre: <strong>{deckStats.totalLands}</strong> (
                        {Math.round(
                          (deckStats.totalLands / deckStats.totalCards) * 100
                        )}
                        %)
                      </Text>
                    </Box>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
